import {Component, h, Host, Prop, State} from '@stencil/core';
import {CompanyViewModel} from "@modules/onBoarding/presentation/viewModel/CompanyViewModel";
import {UserRegistrationViewModel} from "@modules/onBoarding/presentation/viewModel/UserRegistrationViewModel";
import clsx from "clsx";
import {RegisterUserRequest} from "@modules/onBoarding/application/usecase/RegisterUser/RegisterUserRequest";
import {
    ValidateUserRegistrationInfo
} from "@modules/onBoarding/application/usecase/ValidateUserRegistrationInfo/ValidateUserRegistrationInfo";
import {RegisterUser} from "@modules/onBoarding/application/usecase/RegisterUser/RegisterUser";
import {
    CheckEmailAlreadyExists
} from "@modules/onBoarding/application/usecase/CheckEmailAlreadyExists/CheckEmailAlreadyExists";

type UserRegistrationState = {
    businessEmail: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    phoneRegion: string;
    termsAndConditions: boolean;
};

@Component({
    tag: 'user-registration-view',
    styleUrl: 'user-registration-view.css',
    scoped: true,
})
export class UserRegistrationView {

    @Prop() company: CompanyViewModel
    @Prop() viewModel: UserRegistrationViewModel = null;

    private readonly defaultPhoneRegion = this.viewModel.phoneCodes.filter(phoneCode => phoneCode.selected)[0] || this.viewModel.phoneCodes[0];

    private validateUserRegistrationInfoUseCase: ValidateUserRegistrationInfo;
    private registerUserUseCase: RegisterUser;
    private checkEmailAlreadyExistsUseCase: CheckEmailAlreadyExists;

    @State() userRegistrationState: UserRegistrationState = {
        businessEmail: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        phoneRegion: this.defaultPhoneRegion.value,
        termsAndConditions: false,
    };
    @State() errors: { [key: string]: string } = {};

    async componentWillLoad() {
        const module = await import('@modules/onBoarding');
        this.validateUserRegistrationInfoUseCase = module.validateUserRegistrationInfo;
        this.registerUserUseCase = module.registerUser;
        this.checkEmailAlreadyExistsUseCase = module.checkEmailAlreadyExists;
    }

    private readonly handleFormChange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const updatedKey = target.id;
        let updatedValue = updatedKey === "termsAndConditions" ? target.checked : target.value;

        this.updateField(updatedKey, updatedValue);
        await this.updateErrors(updatedKey);
    }

    private updateField(key: string, value: string | boolean) {
        this.userRegistrationState = {
            ...this.userRegistrationState,
            [key]: value
        };
    }

    private async updateErrors(updatedKey: string) {
        const result = await this.validateUserRegistrationInfoUseCase.execute({
            field: updatedKey as "businessEmail" | "firstName" | "lastName" | "phoneNumber" | "termsAndConditions",
            value: this.userRegistrationState[updatedKey]
        });

        this.errors[updatedKey] = null;
        if (!result.success) {
            this.errors = {...this.errors, [updatedKey]: this.viewModel.errorMessages[updatedKey + "ErrorMessages"][result.errorKey]};
            return;
        }

        if (updatedKey == 'businessEmail') {
            await this.checkEmailAlreadyExists(this.userRegistrationState[updatedKey]);
        }
    }

    private async checkEmailAlreadyExists(email: string) {
        const {doesAlreadyExist} = await this.checkEmailAlreadyExistsUseCase.execute(email);

        if (doesAlreadyExist) {
            this.errors = {
                ...this.errors,
                ["businessEmail"]: this.viewModel.errorMessages.businessEmailErrorMessages.alreadyRegistered
            };
        }
    }

    private readonly handleSubmit = async (e: Event) => {
        e.preventDefault();

        if (Object.values(this.errors).some(value => value !== null)) {
            return;
        }

        const registerUserRequest: RegisterUserRequest = {...this.userRegistrationState}
        const userRegistrationResult = await this.registerUserUseCase.execute(registerUserRequest);

        const errors = {};

        Object.entries(userRegistrationResult).forEach((error) => {
            const inputKey = error[0];
            const errorKey = error[1];
            const inputErrorMessagesKey = inputKey + 'ErrorMessages';

            errors[inputKey] = this.viewModel.errorMessages[inputErrorMessagesKey][errorKey];
        });

        this.errors = errors;
    }

    private renderPhoneRegionSelect() {
        return (
            <select id="phoneRegion" name="phoneRegion" class="custom-select"
                    data-testid="onboarding.user-registration.form.phoneCodes"
                    required
                    aria-required="true"
            >
                {
                    this.viewModel.phoneCodes.map((phoneCode) => (
                        <option key={phoneCode.value} value={phoneCode.value} selected={phoneCode.selected}
                                innerHTML={phoneCode.text}>
                        </option>
                    ))
                }
            </select>
        );
    }

    getInputClasses(element: string) {
        return clsx(
            "form-control",
            {
                "is-invalid": !!this.errors[element],
            }
        )
    }

    render() {
        return (
            <Host>
                <div id="registration-view" class="d-flex justify-content-center bg-primary position-relative">
                    <div class="container position-absolute">
                        <div id="content-container" class="mx-auto">
                            <svg class="my-3" aria-hidden="true" width="100%" height="2rem">
                                <use
                                    data-href={this.viewModel.taglineHref}></use>
                            </svg>
                            <div class="card shadow container pt-4 pb-5">
                                <div class="ml-4">
                                    <a href={this.viewModel.modifySearchLink}>{this.viewModel.modifySearch}</a>
                                </div>
                                <form class="col-8 offset-2" data-testid="onboarding.user-registration.form"
                                      onSubmit={this.handleSubmit}>
                                    <nav class="my-4">
                                    </nav>
                                    <h1 class="title_sm mb-4">{this.viewModel.title}</h1>
                                    <div class="px-3 pt-3">
                                        <company-infos company={this.company}></company-infos>
                                    </div>
                                    <hr class="bg-lyreco-green mb-4"/>
                                    <h2 class="title_xs pt-2 mb-3">{this.viewModel.subtitle}</h2>
                                    <div class="form-group">
                                        <label class="text-primary font-weight-bold"
                                               htmlFor="businessEmail">{this.viewModel.businessEmailLabel} *</label>
                                        <div class="mb-1">
                                            <input type="text" class={this.getInputClasses("businessEmail")}
                                                   name="businessEmail"
                                                   id="businessEmail"
                                                   placeholder={this.viewModel.businessEmailPlaceholder}
                                                   data-testid="onboarding.user-registration.form.businessEmail"
                                                   value={this.userRegistrationState.businessEmail}
                                                   onBlur={this.handleFormChange}
                                                   required
                                                   aria-required="true"
                                            />
                                            {
                                                this.errors.businessEmail && (
                                                    <p data-testid="onboarding.user-registration.form.businessEmail.error"
                                                       class="invalid-feedback block">
                                                        {this.errors.businessEmail}
                                                    </p>
                                                )
                                            }
                                        </div>
                                        <span>{this.viewModel.businessEmailInfoText}</span>
                                    </div>
                                    <div class="d-flex">
                                        <div class="form-group w-50">
                                            <label class="text-primary font-weight-bold"
                                                   htmlFor="firstName">{this.viewModel.firstNameLabel} *</label>

                                            <div>
                                                <input type="text" class={this.getInputClasses("firstName")}
                                                       name="firstName"
                                                       id="firstName"
                                                       placeholder={this.viewModel.firstNamePlaceholder}
                                                       data-testid="onboarding.user-registration.form.firstName"
                                                       value={this.userRegistrationState.firstName}
                                                       onBlur={this.handleFormChange}
                                                       required
                                                       aria-required="true"
                                                />
                                                {
                                                    this.errors.firstName && (
                                                        <div data-testid="onboarding.user-registration.form.firstName.error"
                                                             class="invalid-feedback">
                                                            {this.errors.firstName}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div class="pl-3 form-group w-50">
                                            <label class="text-primary font-weight-bold"
                                                   htmlFor="lastName">{this.viewModel.lastNameLabel} *</label>

                                            <div>
                                                <input type="text" class={this.getInputClasses("lastName")}
                                                       name="lastName"
                                                       id="lastName"
                                                       placeholder={this.viewModel.lastNamePlaceholder}
                                                       data-testid="onboarding.user-registration.form.lastName"
                                                       value={this.userRegistrationState.lastName}
                                                       onBlur={this.handleFormChange}
                                                       required
                                                       aria-required="true"
                                                />
                                                {
                                                    this.errors.lastName && (
                                                        <div data-testid="onboarding.user-registration.form.lastName.error"
                                                             class="invalid-feedback">
                                                            {this.errors.lastName}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    <div class="form-group">
                                        <label class="text-primary font-weight-bold"
                                               htmlFor="phoneNumber">{this.viewModel.phoneNumberLabel} *</label>
                                        <div id="phone-input" class="phone-input input-group">
                                            <div class="input-group-prepend">
                                                {this.renderPhoneRegionSelect()}
                                            </div>
                                            <input id="phoneNumber"
                                                   name="phoneNumber"
                                                   type="text" class={this.getInputClasses("phoneNumber")}
                                                   placeholder={this.viewModel.phoneNumberPlaceholder}
                                                   data-testid="onboarding.user-registration.form.phoneNumber"
                                                   value={this.userRegistrationState.phoneNumber}
                                                   onBlur={this.handleFormChange}
                                                   required
                                                   aria-required="true"
                                            />
                                            {
                                                this.errors.phoneNumber && (
                                                    <div data-testid="onboarding.user-registration.form.phoneNumber.error"
                                                         class="invalid-feedback">
                                                        {this.errors.phoneNumber}
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </div>

                                    <span class="small text_secondary">* {this.viewModel.mandatoryFields}</span>

                                    <hr class="bg-lyreco-green"/>

                                    <div class="form-group custom-control custom-checkbox mb-3">
                                        <input type="checkbox" class="custom-control-input"
                                               id="termsAndConditions"
                                               checked={this.userRegistrationState.termsAndConditions}
                                               onBlur={this.handleFormChange}
                                               data-testid="onboarding.user-registration.form.termsAndConditions"
                                               required
                                        />
                                        <label class="custom-control-label" htmlFor="termsAndConditions">
                                            {this.viewModel.termsAndConditionsText}&nbsp;
                                            <a href={this.viewModel.termsAndConditionsLink} target="_blank">
                                                {this.viewModel.termsAndConditionsLinkLabel}
                                            </a> *
                                        </label>
                                        {
                                            this.errors.termsAndConditions && (
                                                <div data-testid="onboarding.user-registration.form.termsAndConditions.error"
                                                     class="invalid-feedback d-block">
                                                    {this.errors.termsAndConditions}
                                                </div>
                                            )
                                        }
                                    </div>

                                    <button class="btn btn-primary text-white w-100" type="submit"
                                            data-testid="onboarding.user-registration.form.submit"
                                    >
                                        {this.viewModel.completeMyRegistration}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
